/**
 * Utility functions for image cropping operations
 */

/**
 * Helper function to get rotated size
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} rotation - Rotation in degrees
 * @returns {Object} - New dimensions after rotation
 */
const rotateSize = (width, height, rotation) => {
    const rotRad = (rotation * Math.PI) / 180;
    return {
        width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
        height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
};

/**
 * Creates a canvas element from an image with the specified crop area
 * Following the official react-easy-crop method for handling rotation
 * @param {string} imageSrc - The source URL of the image
 * @param {Object} cropArea - The crop area with x, y, width, height in pixels
 * @param {number} rotation - Rotation in degrees
 * @returns {Promise<HTMLCanvasElement>} - Promise that resolves to a canvas element
 */
export const createCroppedCanvas = (imageSrc, cropArea, rotation = 0) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';

        image.onload = () => {
            // First create a canvas for the rotated image (if needed)
            if (rotation === 0) {
                // No rotation, simple crop
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = cropArea.width;
                canvas.height = cropArea.height;

                ctx.drawImage(
                    image,
                    cropArea.x, cropArea.y, cropArea.width, cropArea.height,
                    0, 0, cropArea.width, cropArea.height
                );

                resolve(canvas);
            } else {
                // With rotation, follow the official method
                const rotatedCanvas = document.createElement('canvas');
                const rotatedCtx = rotatedCanvas.getContext('2d');

                const rotRad = (rotation * Math.PI) / 180;

                // Calculate bounding box of the rotated image
                const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

                // Set canvas size to match the bounding box
                rotatedCanvas.width = bBoxWidth;
                rotatedCanvas.height = bBoxHeight;

                // Translate canvas context to center to allow rotating around the center
                rotatedCtx.translate(bBoxWidth / 2, bBoxHeight / 2);
                rotatedCtx.rotate(rotRad);
                rotatedCtx.translate(-image.width / 2, -image.height / 2);

                // Draw rotated image
                rotatedCtx.drawImage(image, 0, 0);

                // Now create the cropped canvas
                const croppedCanvas = document.createElement('canvas');
                const croppedCtx = croppedCanvas.getContext('2d');

                // Set the size of the cropped canvas
                croppedCanvas.width = cropArea.width;
                croppedCanvas.height = cropArea.height;

                // Draw the cropped image onto the new canvas
                croppedCtx.drawImage(
                    rotatedCanvas,
                    cropArea.x, cropArea.y, cropArea.width, cropArea.height,
                    0, 0, cropArea.width, cropArea.height
                );

                resolve(croppedCanvas);
            }
        };

        image.onerror = () => {
            reject(new Error('Failed to load image'));
        };

        image.src = imageSrc;
    });
};/**
 * Converts a canvas to a Blob
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @param {string} type - The image type (default: 'image/png')
 * @param {number} quality - The image quality (0-1, for JPEG)
 * @returns {Promise<Blob>} - Promise that resolves to a Blob
 */
export const canvasToBlob = (canvas, type = 'image/png', quality = 0.92) => {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to create blob from canvas'));
                }
            },
            type,
            quality
        );
    });
};

/**
 * Creates a cropped image blob from the original image and crop parameters from react-easy-crop
 * @param {string} imageSrc - The source URL of the image
 * @param {Object} cropParams - The crop parameters from react-easy-crop
 * @param {string} outputType - The output image type (default: 'image/png')
 * @param {number} quality - The image quality (0-1, for JPEG)
 * @returns {Promise<Blob>} - Promise that resolves to the cropped image blob
 */
export const getCroppedImg = async (
    imageSrc,
    cropParams,
    outputType = 'image/png',
    quality = 0.92
) => {
    try {
        // Ensure we have the required crop parameters
        if (!cropParams.crop) {
            throw new Error('Missing crop parameters');
        }

        const canvas = await createCroppedCanvas(
            imageSrc,
            cropParams.crop,     // This should be croppedAreaPixels from react-easy-crop
            cropParams.rotation || 0
        );

        const blob = await canvasToBlob(canvas, outputType, quality);
        return blob;
    } catch (error) {
        console.error('Error creating cropped image:', error);
        throw error;
    }
};

/**
 * Alternative cropping function that processes raw crop data from react-easy-crop
 * This function manually applies zoom and crop calculations
 * @param {string} imageSrc - The source URL of the image
 * @param {Object} cropArea - The crop area in percentages (croppedArea from react-easy-crop)
 * @param {Object} cropPixels - The crop area in pixels (croppedAreaPixels from react-easy-crop)
 * @param {number} rotation - Rotation in degrees
 * @param {number} zoom - Zoom level
 * @param {string} outputType - The output image type
 * @param {number} quality - The image quality (0-1, for JPEG)
 * @returns {Promise<Blob>} - Promise that resolves to the cropped image blob
 */
export const getCroppedImgAdvanced = async (
    imageSrc,
    cropArea,
    cropPixels,
    rotation = 0,
    zoom = 1,
    outputType = 'image/png',
    quality = 0.92
) => {
    try {
        // Use the corrected createCroppedCanvas function that properly handles rotation
        const canvas = await createCroppedCanvas(imageSrc, cropPixels, rotation);
        const blob = await canvasToBlob(canvas, outputType, quality);
        return blob;
    } catch (error) {
        console.error('Error creating cropped image:', error);
        throw error;
    }
};/**
 * Creates a File object from a Blob with a filename
 * @param {Blob} blob - The image blob
 * @param {string} originalFilename - The original filename
 * @param {string} suffix - Suffix to add to filename (default: '_cropped')
 * @returns {File} - File object
 */
export const createFileFromBlob = (blob, originalFilename, suffix = '_cropped') => {
    const extension = originalFilename.split('.').pop();
    const nameWithoutExt = originalFilename.substring(0, originalFilename.lastIndexOf('.'));
    const newFilename = `${nameWithoutExt}${suffix}.${extension}`;

    return new File([blob], newFilename, {
        type: blob.type,
        lastModified: Date.now(),
    });
};

/**
 * Validates if an image format is supported for cropping
 * @param {string} mimeType - The MIME type of the image
 * @returns {boolean} - True if supported
 */
export const isCropSupported = (mimeType) => {
    const supportedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
        'image/bmp',
        'image/gif'
    ];
    return supportedTypes.includes(mimeType.toLowerCase());
};

/**
 * Gets the optimal output format based on input format
 * @param {string} inputMimeType - The input image MIME type
 * @returns {string} - The optimal output MIME type
 */
export const getOptimalOutputFormat = (inputMimeType) => {
    // Keep PNG for images with transparency
    if (inputMimeType === 'image/png') {
        return 'image/png';
    }

    // Convert other formats to JPEG for better compression
    return 'image/jpeg';
};
